import type { ControllerProps } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { IPaymentFieldDefinition, IPaymentMethod } from '@endeavour/payment-integration';
import { PaymentFieldType } from '@endeavour/payment-integration';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';

import { config } from '../../../config/config';
import { Dropdown } from '../../shared/components/dropdown/dropdown';
import { DisplayType } from '../../shared/components/form-builder/form-builder';
import { FormattedMessage } from '../../shared/components/formatted-message/formatted-message';
import { Input } from '../../shared/components/input/input';
import { slugify } from '../../shared/utils/strings';
import { CustomField } from '../components/custom-field/custom-field';
import { HostedField } from '../components/hosted-field/hosted-field';

export function getComponentFromPaymentField(
    field: IPaymentFieldDefinition,
    providerId: string,
    integrationId: string,
) {
    const label = (
        <FormattedMessage
            id={`cashier.method.${field.name.toLowerCase()}.label`}
            defaultMessage={field.name}
        />
    );

    switch (field.type) {
        case PaymentFieldType.HOSTED: {
            return {
                name: field.name,
                type: DisplayType.FIELD,
                component: Controller,
                label,
                props: {
                    render: ({ field: { onChange, onBlur, value } }) => (
                        <HostedField
                            name={field.name}
                            providerId={providerId}
                            integrationId={integrationId}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                    ),
                } as ControllerProps,
                isVisible: field.isVisible,
            };
        }
        case PaymentFieldType.CUSTOM:
            return {
                name: field.name,
                type: DisplayType.FIELD,
                component: Controller,
                props: {
                    render: ({ field: { onChange } }) => {
                        return (
                            <CustomField
                                name={field.name}
                                providerId={providerId}
                                integrationId={integrationId}
                                onChange={onChange}
                            />
                        );
                    },
                } as ControllerProps,
                isVisible: field.isVisible,
            };
        case PaymentFieldType.PASSWORD:
            return {
                name: field.name,
                type: DisplayType.FIELD,
                component: Input,
                label,
                props: {
                    type: 'password',
                },
                isVisible: field.isVisible,
            };
        case PaymentFieldType.SELECT:
            return {
                name: field.name,
                type: DisplayType.FIELD,
                component: Dropdown,
                label,
                props: {
                    items: field.options?.map((option) => {
                        return {
                            name: option.name,
                            value: option.value,
                            id: option.value,
                            translationKey: `cashier.${field.name}.${slugify(option.value)}.label`,
                        };
                    }),
                },
                isVisible: field.isVisible,
            };
    }

    return {
        name: field.name,
        type: DisplayType.FIELD,
        label,
        component: Input,
        isVisible: field.isVisible,
    };
}

export function addAmountToSchema(paymentMethod: IPaymentMethod, methodsSchema?: ZodTypeAny) {
    const min = paymentMethod?.min ? paymentMethod.min : config.CASHIER.default.DEFAULT_LIMITS.MIN;
    const max = paymentMethod?.max ? paymentMethod.max : config.CASHIER.default.DEFAULT_LIMITS.MAX;

    const amountSchema = z.object({
        amount: z
            .number()
            .min(min, 'cashier.select-amount.under-than-min-amount')
            .max(max, 'cashier.select-amount.over-than-max-amount'),
    });

    if (!methodsSchema) {
        return amountSchema;
    }

    return z.intersection(methodsSchema, amountSchema);
}
